import { FC, PropsWithChildren, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchProductsQuery } from 'src/services/search_products/useSearchProductsQuery';
import { createContext, useContextSelector } from 'use-context-selector';
import { assertSearchProductAncestor } from './searchProductContext.guards';
import { SearchProductQueryContextShape } from './searchProductContext.types';
import {
  buildSearchProductsRequest,
  getHasNoPrimarySearchResults,
  getIsPrimarySearchPending,
  getLoadingProductsCombined,
} from './searchProductContext.utils';
import { SearchProductFiltersContext } from './SearchProductFiltersContext';

export const SearchProductQueryContext = createContext<SearchProductQueryContextShape>(
  {} as SearchProductQueryContextShape
);

export const SearchProductQueryProvider: FC<PropsWithChildren> = ({ children }): ReactElement => {
  const [searchParams] = useSearchParams();
  const searchTerm = searchParams.get('q') ?? '';
  const trimmedSearchTerm = searchTerm.trim();

  const dataState = useContextSelector(SearchProductFiltersContext, (context) => context.dataState);
  const effectiveRefineSearch = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.effectiveRefineSearch
  );
  const isRefineSearchActive = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.isRefineSearchActive
  );
  const hasActiveFilters = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.hasActiveFilters
  );
  const toolbarFilters = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.toolbarFilters
  );
  const setDataState = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setDataState
  );
  assertSearchProductAncestor(
    setDataState,
    'SearchProductFiltersProvider',
    'SearchProductQueryProvider'
  );

  const searchProductsRequest = useMemo(
    () =>
      buildSearchProductsRequest({
        dataState,
        trimmedSearchTerm,
        effectiveRefineSearch,
        toolbarFilters,
      }),
    [dataState, effectiveRefineSearch, toolbarFilters, trimmedSearchTerm]
  );

  const {
    data: searchProductsResponse,
    isLoading: loadingSearchProducts,
    isFetching: isFetchingSearchProducts,
    isPending: isPendingSearchProducts,
    isError: isSearchProductsError,
    refetch: refetchSearchProducts,
  } = useSearchProductsQuery({
    request: searchProductsRequest,
    enabled: Boolean(trimmedSearchTerm),
  });

  const [settledSearchTerm, setSettledSearchTerm] = useState(trimmedSearchTerm);

  useEffect(() => {
    if (!isFetchingSearchProducts && !isPendingSearchProducts) {
      setSettledSearchTerm(trimmedSearchTerm);
    }
  }, [isFetchingSearchProducts, isPendingSearchProducts, trimmedSearchTerm]);

  const products = useMemo(
    () => searchProductsResponse?.items ?? [],
    [searchProductsResponse?.items]
  );
  const productsTotal = searchProductsResponse?.totalRecords ?? 0;

  const isPrimarySearchPending = useMemo(
    () =>
      getIsPrimarySearchPending({
        trimmedSearchTerm,
        settledSearchTerm,
        isRefineSearchActive,
        hasActiveFilters,
        isPendingSearchProducts,
        loadingSearchProducts,
        isFetchingSearchProducts,
        searchProductsResponse,
      }),
    [
      trimmedSearchTerm,
      settledSearchTerm,
      isRefineSearchActive,
      hasActiveFilters,
      isPendingSearchProducts,
      loadingSearchProducts,
      isFetchingSearchProducts,
      searchProductsResponse,
    ]
  );

  const hasNoPrimarySearchResults = useMemo(
    () =>
      getHasNoPrimarySearchResults({
        trimmedSearchTerm,
        isRefineSearchActive,
        hasActiveFilters,
        isPrimarySearchPending,
        productsTotal,
      }),
    [trimmedSearchTerm, isRefineSearchActive, hasActiveFilters, isPrimarySearchPending, productsTotal]
  );

  const loadingProducts = getLoadingProductsCombined({
    loadingSearchProducts,
    isFetchingSearchProducts,
    productsLength: products.length,
    trimmedSearchTerm,
    isPendingSearchProducts,
    isPrimarySearchPending,
  });

  const fetchProducts = useCallback(
    async () => refetchSearchProducts(),
    [refetchSearchProducts]
  );

  const value = useMemo<SearchProductQueryContextShape>(
    () => ({
      searchTerm,
      trimmedSearchTerm,
      products,
      productsTotal,
      loadingProducts,
      isPrimarySearchPending,
      hasNoPrimarySearchResults,
      isSearchProductsError,
      fetchProducts,
    }),
    [
      searchTerm,
      trimmedSearchTerm,
      products,
      productsTotal,
      loadingProducts,
      isPrimarySearchPending,
      hasNoPrimarySearchResults,
      isSearchProductsError,
      fetchProducts,
    ]
  );

  return (
    <SearchProductQueryContext.Provider value={value}>{children}</SearchProductQueryContext.Provider>
  );
};
